/**
 * The bottom sheet of Boards 14 B and 14 C — *Navigate to …* and *Log the
 * call?* — drawn as the boards draw it: the page dimmed behind (ink at 38 %),
 * a white sheet with 20 px top corners and a soft shadow, a 40 × 4 handle,
 * a header with an optional 34 px tile, the title at 15.5/700 over its line
 * at 12, and a 32 px round ✕; a body at 14/16 and, when there is one, a
 * footer under a hairline.
 *
 * The same numbers as Activity's `BottomSheet` (`activity/sheet.tsx`), with
 * the two things these sheets need that it has no place for: the tile before
 * the title (Board 14 C's call) and the height each board draws it at
 * (14 B: 390, 14 C: 580), which is taller than what is in it.
 */
import { useEffect, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../../lib/cn.js'
import { escapeIsFor, watchOverlayKeys } from '../../components/overlays.js'
import { Glyph } from '../glyphs.js'
import { useBackLayer } from '../use-activity-history.js'

/** Above the stop page (4) and the scope screens (4). */
export const SHEET_LAYER = 5

export function StopSheet({
  title, sub, tile, children, footer, onClose, label, minHeight,
}: {
  title: ReactNode
  sub?: ReactNode
  /** The 34 px tile before the title (the purple call tile of Board 14 C). */
  tile?: ReactNode
  children: ReactNode
  footer?: ReactNode
  onClose: () => void
  /** The dialog's accessible name, when the title is not plain text. */
  label?: string
  /** The height the board draws it at, where it is taller than what is in it (14 B: 390, 14 C: 580). */
  minHeight?: number
}) {
  /* Back closes it, as it closes every sheet on a phone (Aaron, Oct 26). */
  useBackLayer(onClose)
  useEffect(() => {
    watchOverlayKeys()
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape' && escapeIsFor(SHEET_LAYER)) onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return createPortal(
    <div className="fixed inset-0 z-[70] flex flex-col justify-end [line-height:normal]" data-overlay-layer={SHEET_LAYER}>
      <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0 bg-scrim/[.38]" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={label ?? (typeof title === 'string' ? title : undefined)}
        className="relative flex max-h-[88dvh] flex-col overflow-hidden rounded-[20px_20px_0_0] bg-surface shadow-[0_-10px_30px_rgba(20,20,10,.12)]"
        style={minHeight ? { minHeight: `min(${minHeight}px, 88dvh)` } : undefined}
      >
        <div className="flex justify-center pb-0.5 pt-2">
          <i aria-hidden className="block h-1 w-10 rounded-full bg-line-strong" />
        </div>
        <div className="flex items-center gap-3 border-b border-line-faint px-4 pb-3 pt-2.5">
          {tile}
          <div className="flex min-w-0 flex-col">
            <b className="text-[15.5px] leading-[1.3]">{title}</b>
            {sub && <span className="text-[12px] text-text-muted">{sub}</span>}
          </div>
          <span className="flex-1" />
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-line text-text-muted"
          >
            <Glyph name="close" size={15} />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3.5">{children}</div>
        {footer && (
          <div className={cn('flex shrink-0 gap-2.5 border-t border-line px-4 pt-3',
            'pb-[max(18px,env(safe-area-inset-bottom))]')}>
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  )
}

/** The footer's two 46 px buttons (Boards 14 A, 14 C, 15 B). */
export function SheetAction({
  children, primary = false, onClick, disabled, icon,
}: {
  children: ReactNode
  primary?: boolean
  onClick?: () => void
  disabled?: boolean
  icon?: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'inline-flex h-[46px] w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border px-3.5',
        'text-[14px] font-semibold disabled:opacity-60',
        primary ? 'border-accent bg-accent text-white' : 'border-line bg-surface text-text',
      )}
    >
      {icon}
      {children}
    </button>
  )
}
